import { Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2 } from '@angular/core';
import { AbstractControl } from '@angular/forms';

const FILL_EVENT = 'bpxFill';
const SELECTING_CLASS = 'bpx-fill-selecting';
/**
 * Marks the `<table>` whose cells may also be filled sideways. Opt-in per table: a column holds
 * one kind of value all the way down, so a vertical fill is always sensible, while a row crosses
 * unrelated columns.
 */
const FILL_ACROSS_TABLE = 'table[data-bpx-fill-across]';
/**
 * Marks the `<table>` whose dropdown cells may also be filled sideways, for a row whose dropdowns
 * all offer the same options. Implies `data-bpx-fill-scroll` behaviour like `data-bpx-fill-across`.
 */
const FILL_ACROSS_SELECT_TABLE = 'table[data-bpx-fill-across-select]';
/**
 * Marks the `<table>` whose drags scroll its scrollport on reaching an edge, in either direction.
 * Implied by `data-bpx-fill-across`, since a wide table cannot be filled across without it.
 */
const FILL_SCROLL_TABLE = 'table[data-bpx-fill-scroll]';
/** How near the scrollport edge a drag must get before the table scrolls after it. */
const EDGE_SCROLL_ZONE_PX = 36;
const EDGE_SCROLL_STEP_PX = 18;
/** Chrome reports this when an input carries no `maxlength`. */
const NO_MAX_LENGTH = 524288;

/** What kind of control a cell holds. A fill only ever lands on a cell of its own kind. */
type FillKind = 'text' | 'select' | 'date';
type FillAxis = 'vertical' | 'horizontal';

interface FillDetail {
  value: unknown;
  kind: FillKind;
}

/**
 * Adds an Excel-style fill handle to editable table cells.
 *
 * Apply to any `<td>` that wraps an editable form control and pass the corresponding
 * `AbstractControl` as the binding value. A small square handle appears at the bottom-right
 * corner on hover; dragging it down (or up) over cells in the same column fills them with the
 * source value.
 *
 * On a table marked `data-bpx-fill-across`, the handle also drags sideways to fill along the row.
 * A sideways fill starts only from a text cell and lands only on text cells, so it cannot push a
 * reagent lot number into a dropdown of assay names, into the coupling-date picker, or into the
 * row's checkbox. A diagonal drag locks to whichever way it has travelled further.
 *
 * On a table marked `data-bpx-fill-scroll` — which `data-bpx-fill-across` implies — a drag that
 * reaches the edge of the table's scrollport scrolls it, in whichever direction the drag is
 * running, so a fill can reach rows and columns that were out of sight when it started.
 *
 * @example
 * <td [bpxFillHandle]="formArray.at(rowIndex).get('fieldName')">
 *   <input matInput formControlName="fieldName" />
 * </td>
 */
@Directive({ selector: 'td[bpxFillHandle]' })
export class FillHandleDirective implements OnDestroy {
  @Input('bpxFillHandle') fillControl: AbstractControl | null = null;

  private readonly handle: HTMLElement;
  private isDragging = false;
  private sourceTd!: HTMLTableCellElement;
  private sourceKind: FillKind = 'text';
  private allowsHorizontal = false;
  private highlighted: HTMLElement[] = [];
  private removeMove: (() => void) | null = null;
  private removeUp: (() => void) | null = null;
  private scrollPort: HTMLElement | null = null;
  private scrollFrame: number | null = null;
  private scrollStep = { x: 0, y: 0 };
  private lastPointer = { x: 0, y: 0 };

  constructor(
    private readonly el: ElementRef<HTMLTableCellElement>,
    private readonly renderer: Renderer2
  ) {
    const currentPosition = this.getCurrentPosition();
    // Sticky columns are laid out via CSS classes; never override them with
    // inline positioning because that breaks header/body alignment.
    const isStickyColumn = this.el.nativeElement.classList.contains('sticky-col');
    // Preserve existing positioned layouts and only establish a positioning
    // context for static, non-sticky cells.
    if (!isStickyColumn && (currentPosition === 'static' || !currentPosition)) {
      this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    }
    this.renderer.setStyle(this.el.nativeElement, 'overflow', 'visible');

    this.handle = this.renderer.createElement('span');
    this.renderer.addClass(this.handle, 'bpx-fill-handle');
    // Hardcode size/placement inline so component or global stylesheet precedence
    // cannot shrink the fill handle.
    this.renderer.setStyle(this.handle, 'width', '10px');
    this.renderer.setStyle(this.handle, 'height', '10px');
    this.renderer.setStyle(this.handle, 'right', '1px');
    this.renderer.setStyle(this.handle, 'bottom', '1px');
    this.renderer.setStyle(this.handle, 'opacity', '0');
    this.renderer.appendChild(this.el.nativeElement, this.handle);

    this.renderer.listen(this.handle, 'mousedown', (e: MouseEvent) => this.onHandleMouseDown(e));
  }

  private getCurrentPosition(): string {
    if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
      return this.el.nativeElement.style.position || '';
    }
    return window.getComputedStyle(this.el.nativeElement).position || '';
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.fillControl) {
      this.renderer.setStyle(this.handle, 'opacity', '1');
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (!this.isDragging) {
      this.renderer.setStyle(this.handle, 'opacity', '0');
    }
  }

  @HostListener(FILL_EVENT, ['$event'])
  onFill(event: CustomEvent<FillDetail>): void {
    if (!this.fillControl || event.detail.kind !== this.fillKind(this.el.nativeElement)) {
      return;
    }
    this.fillControl.setValue(this.clampToCellLimit(event.detail.value));
  }

  private onHandleMouseDown(e: MouseEvent): void {
    if (!this.fillControl) return;
    e.preventDefault();
    e.stopPropagation();
    this.isDragging = true;
    this.sourceTd = this.el.nativeElement;
    this.sourceKind = this.fillKind(this.sourceTd);
    // Resolved per drag rather than in the constructor: a cell is not in the document yet while
    // its directive is being created, so it has no table to look up.
    this.allowsHorizontal =
      !!this.sourceTd.closest(FILL_ACROSS_TABLE) ||
      (this.sourceKind === 'select' && !!this.sourceTd.closest(FILL_ACROSS_SELECT_TABLE));
    const allowsEdgeScroll = this.allowsHorizontal || !!this.sourceTd.closest(FILL_SCROLL_TABLE);
    this.scrollPort = allowsEdgeScroll ? this.findScrollPort() : null;
    this.lastPointer = { x: e.clientX, y: e.clientY };
    this.removeMove = this.renderer.listen('document', 'mousemove', (me: MouseEvent) => this.onMouseMove(me));
    this.removeUp = this.renderer.listen('document', 'mouseup', (me: MouseEvent) => this.onMouseUp(me));
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.isDragging) return;
    this.lastPointer = { x: e.clientX, y: e.clientY };
    this.highlightTargets();
    this.updateEdgeScroll();
  }

  private onMouseUp(e: MouseEvent): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.lastPointer = { x: e.clientX, y: e.clientY };
    this.detachListeners();
    this.stopEdgeScroll();
    this.renderer.setStyle(this.handle, 'opacity', '0');
    const targets = this.resolveTargets();
    this.clearHighlights();
    if (!this.fillControl) return;

    const detail: FillDetail = { value: this.fillControl.value, kind: this.sourceKind };
    targets.forEach(cell => {
      cell.dispatchEvent(new CustomEvent<FillDetail>(FILL_EVENT, { detail, bubbles: false }));
    });
  }

  private highlightTargets(): void {
    this.clearHighlights();
    this.resolveTargets().forEach(cell => {
      this.renderer.addClass(cell, SELECTING_CLASS);
      this.highlighted.push(cell);
    });
  }

  /** The cells the drag currently covers, source excluded, in the order they will be filled. */
  private resolveTargets(): HTMLTableCellElement[] {
    const rows = this.getBodyRows();
    const sourceRow = rows.indexOf(this.sourceTd.parentElement as HTMLTableRowElement);
    if (sourceRow < 0) return [];

    const targetTd = this.tdAtPoint(this.lastPointer.x, this.lastPointer.y);
    if (!targetTd) return [];
    const targetRow = rows.indexOf(targetTd.parentElement as HTMLTableRowElement);
    if (targetRow < 0) return [];

    const sourceColumn = this.sourceTd.cellIndex;
    const targetColumn = targetTd.cellIndex;
    const axis = this.resolveAxis(sourceRow, sourceColumn, targetRow, targetColumn);
    if (!axis) return [];

    return axis === 'vertical'
      ? this.cellsDownColumn(rows, sourceColumn, sourceRow, targetRow)
      : this.cellsAlongRow(rows[sourceRow], sourceColumn, targetColumn);
  }

  private resolveAxis(
    sourceRow: number,
    sourceColumn: number,
    targetRow: number,
    targetColumn: number
  ): FillAxis | null {
    const sameRow = targetRow === sourceRow;
    const sameColumn = targetColumn === sourceColumn;
    if (sameRow && sameColumn) return null;
    if (!this.canFillAcross) return sameColumn ? 'vertical' : null;
    if (sameRow) return 'horizontal';
    if (sameColumn) return 'vertical';

    // A diagonal drag commits to one axis rather than filling a block, so it follows whichever
    // way it has travelled further from the handle.
    const rect = this.sourceTd.getBoundingClientRect();
    const acrossBy = Math.abs(this.lastPointer.x - (rect.left + rect.width / 2));
    const downBy = Math.abs(this.lastPointer.y - (rect.top + rect.height / 2));
    return acrossBy > downBy ? 'horizontal' : 'vertical';
  }

  /**
   * Sideways fills are text-to-text, or select-to-select on a table that opts in; every other kind
   * of cell fills down its column alone.
   */
  private get canFillAcross(): boolean {
    if (!this.allowsHorizontal) return false;
    return this.sourceKind === 'text' || (this.sourceKind === 'select' && !!this.sourceTd.closest(FILL_ACROSS_SELECT_TABLE));
  }

  private cellsDownColumn(
    rows: HTMLTableRowElement[],
    column: number,
    sourceRow: number,
    targetRow: number
  ): HTMLTableCellElement[] {
    const cells: HTMLTableCellElement[] = [];
    for (let row = Math.min(sourceRow, targetRow); row <= Math.max(sourceRow, targetRow); row++) {
      const cell = rows[row]?.cells[column];
      if (cell && row !== sourceRow) {
        cells.push(cell);
      }
    }
    return cells;
  }

  private cellsAlongRow(
    row: HTMLTableRowElement,
    sourceColumn: number,
    targetColumn: number
  ): HTMLTableCellElement[] {
    const rowCells = Array.from(row?.cells ?? []);
    const cells: HTMLTableCellElement[] = [];
    for (let column = Math.min(sourceColumn, targetColumn); column <= Math.max(sourceColumn, targetColumn); column++) {
      const cell = rowCells[column];
      // Columns of another kind are stepped over rather than ending the drag, so a run of text
      // columns can still be filled past the odd dropdown sitting among them.
      if (cell && column !== sourceColumn && this.isFillable(cell) && this.fillKind(cell) === this.sourceKind) {
        cells.push(cell);
      }
    }
    return cells;
  }

  /**
   * Every cell the directive is attached to carries an injected handle, so a cell without one —
   * the row's checkbox, say — holds nothing to fill and is not worth highlighting.
   */
  private isFillable(cell: HTMLElement): boolean {
    return !!cell.querySelector('.bpx-fill-handle');
  }

  private fillKind(cell: HTMLElement): FillKind {
    if (cell.querySelector('mat-select')) return 'select';
    if (cell.querySelector('mat-datepicker')) return 'date';
    return 'text';
  }

  /** A sideways fill crosses columns with different length caps, and `setValue` ignores `maxlength`. */
  private clampToCellLimit(value: unknown): unknown {
    if (typeof value !== 'string') return value;
    const limit = this.el.nativeElement.querySelector('input')?.maxLength ?? -1;
    return limit > 0 && limit < NO_MAX_LENGTH ? value.slice(0, limit) : value;
  }

  /** The nearest ancestor that actually scrolls, so a long drag can reach the rest of the table. */
  private findScrollPort(): HTMLElement | null {
    if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
      return null;
    }
    let node = this.el.nativeElement.parentElement;
    while (node) {
      const style = window.getComputedStyle(node);
      const scrollsAcross = /(auto|scroll)/.test(style.overflowX) && node.scrollWidth > node.clientWidth;
      const scrollsDown = /(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight;
      if (scrollsAcross || scrollsDown) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  private updateEdgeScroll(): void {
    if (!this.scrollPort) return;
    const rect = this.scrollPort.getBoundingClientRect();
    this.scrollStep = {
      x: this.edgeStep(this.lastPointer.x, rect.left, rect.right),
      y: this.edgeStep(this.lastPointer.y, rect.top, rect.bottom)
    };
    if (this.scrollStep.x || this.scrollStep.y) {
      this.startEdgeScroll();
      return;
    }
    this.stopEdgeScroll();
  }

  private edgeStep(position: number, start: number, end: number): number {
    if (position > end - EDGE_SCROLL_ZONE_PX) return EDGE_SCROLL_STEP_PX;
    if (position < start + EDGE_SCROLL_ZONE_PX) return -EDGE_SCROLL_STEP_PX;
    return 0;
  }

  private startEdgeScroll(): void {
    if (this.scrollFrame !== null || typeof requestAnimationFrame !== 'function') return;
    const step = () => {
      if (!this.isDragging || !this.scrollPort) {
        this.scrollFrame = null;
        return;
      }
      this.scrollPort.scrollLeft += this.scrollStep.x;
      this.scrollPort.scrollTop += this.scrollStep.y;
      // The pointer has not moved but the cells beneath it have, so the range is re-read.
      this.highlightTargets();
      this.scrollFrame = requestAnimationFrame(step);
    };
    this.scrollFrame = requestAnimationFrame(step);
  }

  private stopEdgeScroll(): void {
    if (this.scrollFrame !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.scrollFrame);
    }
    this.scrollFrame = null;
    this.scrollStep = { x: 0, y: 0 };
  }

  private getBodyRows(): HTMLTableRowElement[] {
    const table = this.el.nativeElement.closest('table');
    return table ? Array.from(table.querySelectorAll<HTMLTableRowElement>('tbody tr')) : [];
  }

  private tdAtPoint(x: number, y: number): HTMLTableCellElement | null {
    const prev = this.handle.style.pointerEvents;
    this.handle.style.pointerEvents = 'none';
    const hit = document.elementFromPoint(x, y);
    this.handle.style.pointerEvents = prev;
    return hit ? (hit as HTMLElement).closest<HTMLTableCellElement>('td') : null;
  }

  private clearHighlights(): void {
    this.highlighted.forEach(c => this.renderer.removeClass(c, SELECTING_CLASS));
    this.highlighted = [];
  }

  private detachListeners(): void {
    this.removeMove?.();
    this.removeUp?.();
    this.removeMove = null;
    this.removeUp = null;
  }

  ngOnDestroy(): void {
    this.isDragging = false;
    this.detachListeners();
    this.stopEdgeScroll();
    this.clearHighlights();
  }
}
